import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import UserCard from '../../components/user_card';
import Navbar from '../../components/navbar';
import FooterProfile from '../../components/footer_profile';
import isLoggedIn from '../../utils/isLoggedIn';
import { convertEntitiesDeep } from '../../utils/convertEntities';

const formatPrice = (priceInCents, currency) =>
  typeof priceInCents === 'number'
    ? new Intl.NumberFormat('en-UK', {
        style: 'currency',
        currency: currency || 'EUR',
      }).format(priceInCents / 100)
    : '-';

const QuantilaUserAddPlan = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = sessionStorage.getItem('token');
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [isLoading, setLoading] = useState(true);
  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    isLoggedIn(navigate, dispatch, `/quantila/user/addplan/${id}`);

    let active = true;

    const loadData = async () => {
      try {
        const [userResponse, plansResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API}/admin/user/${id}`, {
            headers: { authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API}/admin/quantila/plans`, {
            headers: { authorization: `Bearer ${token}` },
          }),
        ]);

        if (active) {
          setUser(convertEntitiesDeep(userResponse.data));
          const plansData = convertEntitiesDeep(plansResponse.data);
          const nextPlans = Array.isArray(plansData)
            ? plansData
            : plansData?.plans || [];
          setPlans(nextPlans.filter((plan) => plan.active));
          if (nextPlans.length > 0) {
            setSelectedPlanId(String(nextPlans[0].id));
          }
          setLoading(false);
        }
      } catch (error) {
        toast.error(
          convertEntitiesDeep(error.response?.data?.error || error.message),
        );
        if (active) setLoading(false);
      }
    };

    loadData();

    return () => {
      active = false;
    };
  }, [id]);

  const handleAssignPlan = async () => {
    if (!selectedPlanId) {
      toast.error('Please select a plan.');
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API}/admin/quantila/user/addplan`,
        {
          userId: id,
          planId: Number(selectedPlanId),
        },
        {
          headers: { authorization: `Bearer ${token}` },
        },
      );

      if (response.status === 200 || response.status === 201) {
        toast.success(
          convertEntitiesDeep(response.data.message) ||
            'Plan was successfully assigned.',
        );
        setTimeout(() => navigate(`/user/${id}`), 1200);
      }
    } catch (error) {
      toast.error(
        convertEntitiesDeep(error.response?.data?.error || error.message),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return isLoading ? (
    <>
      <Helmet>
        <title>Badgers SAS: Quantila plan assignment</title>
        <meta name='page' content='quantila plan assignment' />
      </Helmet>
      <div className='loading'>
        <img src='/img/loading.gif' alt='Loading' className='loading-img' />
      </div>
    </>
  ) : (
    <>
      <Helmet>
        <title>Badgers SAS: Quantila plan assignment</title>
        <meta name='page' content='quantila plan assignment' />
      </Helmet>
      <Navbar displayMob={true} />
      <Toaster position='top-center' reverseOrder={false} />
      <div className='container my-4'>
        <div>
          <Link className='page-links' to='/'>
            <FontAwesomeIcon icon='home' />{' '}
          </Link>
          <span className='link-span'>
            <FontAwesomeIcon icon='angle-right' />
          </span>
          <Link className='page-links' to='/quantila/users'>
            QUANTILA USERS{' '}
          </Link>
          <span className='link-span'>
            <FontAwesomeIcon icon='angle-right' />
          </span>
          <span className='link-span'>ADD PLAN</span>
        </div>

        <div className='row g-4 my-3'>
          <div className='col-lg-4'>{user && <UserCard user={user} />}</div>

          <div className='col-lg-8'>
            <div className='card shadow-sm p-4 w-100'>
              <h4 className='h4 mb-3'>Assign Quantila Plan</h4>

              {plans.length > 0 ? (
                plans.map((plan) => (
                  <label
                    key={plan.id}
                    className='d-flex align-items-center justify-content-between border rounded p-3 mb-3'
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      <input
                        type='radio'
                        name='plan'
                        value={plan.id}
                        checked={String(plan.id) === String(selectedPlanId)}
                        onChange={(e) => setSelectedPlanId(e.target.value)}
                      />
                      <span className='ms-2'>
                        <strong>{plan.name}</strong>
                      </span>
                    </div>
                    <div className='text-end'>
                      <div>{formatPrice(plan.price, plan.currency)}</div>
                      <small>{plan.maxRfqPerMonth} RFQs / month</small>
                    </div>
                  </label>
                ))
              ) : (
                <p className='text-muted'>No active plans available.</p>
              )}

              <button
                type='button'
                className='btn btn-primary mt-3'
                onClick={handleAssignPlan}
                disabled={isSubmitting || plans.length === 0}
              >
                {isSubmitting ? 'Adding plan...' : 'Add plan'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <FooterProfile />
    </>
  );
};

export default QuantilaUserAddPlan;
